/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * One dashboard: view it, edit it, or pin it as your home page.
 *
 * View and edit are the same page with a toggle rather than two routes. The
 * layout is the thing being edited and it renders identically either way, so a
 * separate builder route would mean rendering the same grid twice and keeping
 * the two in step.
 *
 * Layout changes save on a debounce. A drag fires onLayoutChange continuously,
 * and a PATCH per frame would be both slow and a good way to lose the last
 * position to a race.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Box, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, MenuItem, Snackbar, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AddRounded, ArrowBackRounded, CheckRounded, ContentCopyRounded,
  DashboardCustomizeRounded, EditOutlined, HomeRounded, RefreshRounded,
  ShareOutlined,
} from '@mui/icons-material';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { useAuth } from 'auth/Auth';
import colors from 'styles/tokens/colors';
import AddWidgetDialog from './AddWidgetDialog';
import DashboardCanvas from './DashboardCanvas';
import ShareDialog from './ShareDialog';

const PERIODS = [
  { value: '', label: 'Report defaults' },
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'LAST_MONTH', label: 'Last month' },
  { value: 'THIS_QUARTER', label: 'This quarter' },
  { value: 'LAST_QUARTER', label: 'Last quarter' },
  { value: 'THIS_YEAR', label: 'This year' },
  { value: 'LAST_N_DAYS:30', label: 'Last 30 days' },
];

const LAYOUT_SAVE_DEBOUNCE_MS = 900;

export default function DashboardPage() {
  const { dashboardId } = useParams();
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [period, setPeriod] = useState('');
  const [isHome, setIsHome] = useState(false);
  const [toast, setToast] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(`/analytics/dashboards/${dashboardId}`);
      setDashboard(data);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 404
        ? 'This dashboard does not exist, or it has not been shared with you.'
        : 'That dashboard could not be opened.');
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, dashboardId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    axiosInstance.get('/analytics/home-dashboard')
      .then(({ data }) => setIsHome(String(data?.dashboard_id) === String(dashboardId)))
      .catch(() => setIsHome(false));
  }, [axiosInstance, dashboardId]);

  /* A date override applied to every widget that has a matching field. The
     engine skips widgets whose object lacks it, so one bar can drive a mixed
     dashboard without blanking the tiles it does not apply to. */
  const overrides = period
    ? { conditions: [{ field: 'created_at', operator: 'relative_range', value: period }] }
    : null;

  const saveLayout = useCallback((layouts) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await axiosInstance.patch(`/analytics/dashboards/${dashboardId}`, { layout: layouts });
        setDashboard((d) => (d ? { ...d, layout: layouts } : d));
      } catch {
        setToast({ severity: 'error',
                   message: 'The new arrangement could not be saved.' });
      }
    }, LAYOUT_SAVE_DEBOUNCE_MS);
  }, [axiosInstance, dashboardId]);

  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const addWidget = async (widget) => {
    try {
      await axiosInstance.post(`/analytics/dashboards/${dashboardId}/widgets`, widget);
      await load();
      setToast({ severity: 'success', message: 'Widget added.' });
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That widget could not be added.') });
    }
  };

  const removeWidget = async (widget) => {
    try {
      await axiosInstance.delete(
        `/analytics/dashboards/${dashboardId}/widgets/${widget.id}`);
      await load();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That widget could not be removed.') });
    }
  };

  const toggleHome = async () => {
    try {
      await axiosInstance.put('/analytics/home-dashboard', {
        dashboard_id: isHome ? null : Number(dashboardId),
      });
      setIsHome(!isHome);
      setToast({ severity: 'success',
                 message: isHome
                   ? 'Unpinned. Your home page goes back to the default.'
                   : 'This is now your home page.' });
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That could not be saved.') });
    }
  };

  const rename = async () => {
    const name = (renaming ?? '').trim();
    if (!name) return;
    try {
      await axiosInstance.patch(`/analytics/dashboards/${dashboardId}`, { name });
      setRenaming(null);
      await load();
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That could not be renamed.') });
    }
  };

  const clone = async () => {
    try {
      const { data } = await axiosInstance.post(
        `/analytics/dashboards/${dashboardId}/clone`, {});
      navigate(`/dashboards/${data.id}`);
      setToast({ severity: 'success', message: 'Copied.' });
    } catch (err) {
      setToast({ severity: 'error', message: readError(err, 'That could not be copied.') });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <Box display="flex" justifyContent="center" py={10}><CircularProgress /></Box>
      </DashboardLayout>
    );
  }

  if (!dashboard) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Alert severity="error">{error}</Alert>
          <MDButton variant="text" color="dark" sx={{ mt: 2 }}
                    onClick={() => navigate('/dashboards')}>
            Back to dashboards
          </MDButton>
        </MDBox>
      </DashboardLayout>
    );
  }

  const canEdit = dashboard.access === 'edit' || dashboard.access === 'manage';

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Box display="flex" alignItems="flex-start" gap={1.5} mb={2.5} flexWrap="wrap">
          <IconButton size="small" onClick={() => navigate('/dashboards')} sx={{ mt: 0.5 }}>
            <ArrowBackRounded fontSize="small" />
          </IconButton>

          <Box flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h4" fontWeight={700} lineHeight={1.2} noWrap>
                {dashboard.name}
              </Typography>
              {isHome && (
                <Chip size="small" icon={<HomeRounded sx={{ fontSize: 13 }} />} label="Home"
                      sx={{ backgroundColor: colors.status.success.background,
                            color: colors.status.success.text, fontWeight: 700 }} />
              )}
              {editing && (
                <Chip size="small" label="Editing"
                      sx={{ backgroundColor: colors.status.warning.background,
                            color: colors.status.warning.text, fontWeight: 700 }} />
              )}
            </Box>
            {dashboard.description && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {dashboard.description}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <TextField
              select size="small" label="Period" value={period}
              onChange={(e) => setPeriod(e.target.value)} sx={{ width: 160 }}
            >
              {PERIODS.map((p) => (
                <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
              ))}
            </TextField>

            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => setRefreshKey((k) => k + 1)}>
                <RefreshRounded fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={isHome ? 'Unpin from home' : 'Set as my home page'}>
              <IconButton size="small" onClick={toggleHome}>
                <HomeRounded fontSize="small"
                             sx={{ color: isHome ? colors.brand.primary : undefined }} />
              </IconButton>
            </Tooltip>

            <MDButton variant="outlined" color="dark" size="small" onClick={clone}>
              <ContentCopyRounded sx={{ mr: 0.5, fontSize: 18 }} /> Copy
            </MDButton>

            {dashboard.access === 'manage' && (
              <MDButton variant="outlined" color="dark" size="small"
                        onClick={() => setSharing(true)}>
                <ShareOutlined sx={{ mr: 0.5, fontSize: 18 }} /> Share
              </MDButton>
            )}

            {canEdit && editing && (
              <MDButton variant="outlined" color="info" size="small"
                        onClick={() => setAdding(true)}>
                <AddRounded sx={{ mr: 0.5, fontSize: 18 }} /> Widget
              </MDButton>
            )}

            {canEdit && (
              <MDButton
                variant="gradient" color={editing ? 'success' : 'info'} size="small"
                onClick={() => setEditing((e) => !e)}
              >
                {editing
                  ? <><CheckRounded sx={{ mr: 0.5, fontSize: 18 }} /> Done</>
                  : <><EditOutlined sx={{ mr: 0.5, fontSize: 18 }} /> Edit</>}
              </MDButton>
            )}
          </Box>
        </Box>

        {editing && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Drag a widget by its title bar, or drag its bottom-right corner to
            resize. Changes save as you go.
          </Alert>
        )}

        <DashboardCanvas
          key={`${dashboardId}-${refreshKey}-${period}`}
          dashboard={dashboard}
          editable={editing}
          overrides={overrides}
          onLayoutChange={saveLayout}
          onRemoveWidget={removeWidget}
          emptyState={
            <Box sx={{ p: 8, textAlign: 'center', border: '1px dashed',
                       borderColor: 'divider', borderRadius: '14px' }}>
              <DashboardCustomizeRounded sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
              <Typography variant="h6" gutterBottom>This dashboard is empty</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Add a chart you built on a report, a number, or a list.
              </Typography>
              {canEdit && (
                <MDButton variant="gradient" color="info"
                          onClick={() => { setEditing(true); setAdding(true); }}>
                  <AddRounded sx={{ mr: 0.75 }} /> Add a widget
                </MDButton>
              )}
            </Box>
          }
        />
      </MDBox>

      <AddWidgetDialog
        open={adding} onClose={() => setAdding(false)} onAdd={addWidget}
        existingKeys={(dashboard.widgets ?? []).map((w) => w.grid_key)}
      />

      <ShareDialog open={sharing} onClose={() => setSharing(false)}
                   resourceType="dashboard" resource={dashboard} />

      <Dialog open={renaming !== null} onClose={() => setRenaming(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 700 }}>Rename dashboard</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth size="small" sx={{ mt: 1 }}
                     value={renaming ?? ''} onChange={(e) => setRenaming(e.target.value)}
                     onKeyDown={(e) => e.key === 'Enter' && rename()} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <MDButton variant="text" color="dark" onClick={() => setRenaming(null)}>Cancel</MDButton>
          <MDButton variant="gradient" color="info" onClick={rename}>Save</MDButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast?.severity ?? 'info'} onClose={() => setToast(null)}>
          {toast?.message}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}

function readError(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}
